/**
 * Component: Predictive Search Results
 * -----------------------------------------------------------------------------
 * Logic related to displaying results from search drawer.
 * @namespace predictiveSearchResults
 */

import classes from '~/config/classes'
import { on } from '~/helpers/utils'
import { misc } from '~/helpers/svg-map'

/**
 * DOM selectors.
 */
const selectors = {
  colorsDiv: '[js-predictive-search-results="colorsDiv"]',
  featuredProductsWrap: '[js-predictive-search-results="featuredProductsWrap"]',
  keyword: '[js-predictive-search-results="keyword"]',
  resultsList: '[js-predictive-search-results="resultsList"]',
  resultsTab: '[js-predictive-search-results="resultsTab"]',
  resultsWrap: '[js-predictive-search-results="resultsWrap"]',
  searchInput: '[data-predictive-search-input]',
}
let resultsObj = []
let suggestedTerms

/**
 * Create a new predictiveSearchResults object.
 */
export default () => {

  /**
   * Node selectors.
   */
  const nodeSelectors = {
    featuredProductsWrap: document.querySelector(selectors.featuredProductsWrap),
    resultsWrap: document.querySelector(selectors.resultsWrap),
    searchInput: document.querySelector(selectors.searchInput),
  }

  /**
   * Build results header.
   * @param {Object || Boolean} results - Results object, or false.
   * @returns {String}
   */
  function buildResultsHeader(results) {
    const strings = window.basis.strings.search
    let products = 0
    let articles = 0
    let pages = 0

    if (results) {
      products = results.products.length
      articles = results.articles.length
      pages = results.pages.length
    }

    const productsString = strings.productsNum.replace('#', products)
    const articlesString = strings.articlesNum.replace('#', articles)
    const pagesString = strings.pagesNum.replace('#', pages)

    const markup = `
      <div class="search-drawer__results-header">
        <button
          class="search-drawer__results-tab"
          type="button"
          js-predictive-search-results="resultsTab"
          data-id="products"
          data-count="${products}"
        >
          <div>
            ${productsString}
          </div>
        </button>

        <button
          class="search-drawer__results-tab"
          type="button"
          js-predictive-search-results="resultsTab"
          data-id="articles"
          data-count="${articles}"
        >
          <div>
            ${articlesString}
          </div>
        </button>

        <button
          class="search-drawer__results-tab"
          type="button"
          js-predictive-search-results="resultsTab"
          data-id="pages"
          data-count="${pages}"
        >
          <div>
            ${pagesString}
          </div>
        </button>
      </div>

      <div
        class="search-drawer__results-list"
        js-predictive-search-results="resultsList"
      ></div>
    `

    return markup
  }

  /**
   * Build empty tab content.
   * @param {String} id - Button id.
   */
  function buildEmptyResults(id) {
    const string = encodeURI(window.basis.strings.search.notFound)
    const text = string.replace('results', id)
    let extraClass = ''

    if (id === 'results') {
      extraClass = 'search-drawer__not-found-main'
    }

    const markup = `
      <h3
        class="
          search-drawer__suggested-title
          search-drawer__not-found-title
          ${extraClass}
        "
      >
        ${decodeURI(text)}
      </h3>
    `

    return markup
  }

  /**
   * Get amount of colors per product and add copy to product card.
   * @param {Element} div - Color div to add text to.
   */
  function getDivColors(div) {
    const handle = div.dataset.handle

    fetch(`/products/${handle}.json`)
      .then((response) => response.json())
      .then((response) => {
        const colorOption = response.product.options.find((option) => option.name === 'Color')

        if (!colorOption) {
          return
        }

        const { colorsAvailable } = window.basis.strings.search

        div.innerText = colorsAvailable.replace('#', colorOption.values.length)
      })
  }

  /**
   * Build product list on 'products' tab click.
   * @param {Object} results - Products results.
   * @param {Element} list - Element to append markup to.
   */
  function buildProductsList(results, list) {
    let markup = ''

    results.forEach((product) => {
      markup += `
        <a href="/products/${product.handle}">
          <div class="search-drawer__featured-product-item">
            <div class="search-drawer__featured-product-image-wrap">
              <img src="${product.image}">
            </div>

            <div class="search-drawer__featured-product-details">
              <div class="search-drawer__featured-product-title">
                ${product.title}
              </div>

              <div class="search-drawer__featured-product-price">
                $${product.price}
              </div>

              <div
                class="search-drawer__featured-product-colors"
                data-handle="${product.handle}"
                js-predictive-search-results="colorsDiv"
              ></div>
            </div>
          </div>
        </a>
      `
    })

    list.insertAdjacentHTML('beforeend', markup)

    const colorsDiv = document.querySelectorAll(selectors.colorsDiv)

    colorsDiv.forEach((div) => getDivColors(div))
  }

  /**
   * Format blog post date.
   * @param {String} stamp - Timestamp of published_at date.
   * @returns {String}
   */
  function formatArticleDate(stamp) {
    const date = new Date(stamp)

    let dateFormatted = date.toLocaleDateString(
      'en-us',
      {
        weekday: 'short',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      },
    ).split(',')

    dateFormatted = [...dateFormatted].slice(1).join(',').trim()

    return dateFormatted
  }

  /**
   * Build article badge if tag exists.
   * @param {Array} tags - Articles tags.
   * @returns {String}
   */
  function buildArticleBadge(tags) {
    const badgeTag = tags.find((tag) => tag.includes('badge:'))
    let badge = ''

    if (badgeTag) {
      const array = badgeTag.split(':')

      badge = `
        <div class="search-drawer__article-badge">
          <span
            class="search-drawer__article-circle"
            style="background-color: ${array[1]}"
          ></span>

          <span>${array[2]}</span>
        </div>
      `
    }

    return badge
  }

  /**
   * Get article reading time from tags.
   * @param {Array} tags - Articles tags.
   * @returns {String}
   */
  function getReadingTime(tags) {
    const readTag = tags.find((tag) => tag.includes('read:'))

    if (!readTag) {
      return ''
    }

    return readTag.split(':')[1]
  }

  /**
   * Build article list on 'articles' tab click.
   * @param {Object} results - Articles results.
   * @param {Element} list - Element to append markup to.
   */
  function buildArticlesList(results, list) {
    let markup = ''

    results.forEach((article) => {
      const dateFormatted = formatArticleDate(article.published_at)
      const badge = buildArticleBadge(article.tags)
      const readingTime = getReadingTime(article.tags)

      markup += `
        <a
          class="search-drawer__article-link"
          href="${article.url}"
        >
          <div class="search-drawer__article-item">
            ${badge}

            <h4 class="search-drawer__article-title">
              ${article.title}
            </h4>

            <div class="search-drawer__article-footer">
              <span>${dateFormatted}</span>
              <span>${readingTime}</span>
            </div>
          </div>
        </a>
      `
    })

    list.insertAdjacentHTML('beforeend', markup)
  }

  /**
   * Build page list on 'pages' tab click.
   * @param {Object} results - Pages results.
   * @param {Element} list - Element to append markup to.
   */
  function buildPagesList(results, list) {
    let markup = ''

    results.forEach((page) => {
      markup += `
        <a
          class="search-drawer__page-link"
          href="${page.url}"
        >
          <div class="search-drawer__page-item">
            <span class="search-drawer__page-title">
              ${page.title}
            </span>

            <span class="search-drawer__page-arrow">
              ${misc.arrowRight}
            </span>
          </div>
        </a>
      `
    })

    list.insertAdjacentHTML('beforeend', markup)
  }

  /**
   * Get tab content on button click.
   * @param {String} id - Button id.
   */
  function getTabContent(id) {
    const results = resultsObj[id]
    const resultsList = document.querySelector(selectors.resultsList)
    const markup = buildEmptyResults(id)

    resultsList.innerHTML = ''

    if (!results || !results.length) {
      resultsList.insertAdjacentHTML('afterbegin', markup)
      nodeSelectors.featuredProductsWrap.classList.add(classes.active)

      return
    }

    nodeSelectors.featuredProductsWrap.classList.remove(classes.active)

    switch (id) {
      case 'products':
        buildProductsList(results, resultsList)
        break

      case 'articles':
        buildArticlesList(results, resultsList)
        break

      case 'pages':
        buildPagesList(results, resultsList)
    }
  }

  /**
   * Set event listeners on header buttons after render.
   */
  function setHeaderListeners() {
    const resultsTab = document.querySelectorAll(selectors.resultsTab)

    resultsTab.forEach((tab) => {
      on('click', tab, () => {
        if (tab.classList.contains(classes.active)) {
          return
        }

        resultsTab.forEach((el) => {
          el.classList.remove(classes.active)
        })

        tab.classList.add(classes.active)
        getTabContent(tab.dataset.id)
      })
    })
  }

  /**
   * Build main empty results layout.
   */
  function buildEmptyResultsMain() {
    const resultsList = document.querySelector(selectors.resultsList)
    const resultsTab = [...document.querySelectorAll(selectors.resultsTab)]
    const tab = resultsTab.find((el) => el.dataset.id === 'products')
    const heading = buildEmptyResults('results')
    const subtitle = `
      <p class="search-drawer__not-found-sub">
        ${window.basis.strings.search.changeSearchTerms}
      </p>
    `

    tab.classList.add(classes.active)
    resultsList.innerHTML = ''
    resultsList.insertAdjacentHTML('afterbegin', heading)
    resultsList.insertAdjacentHTML('beforeend', subtitle)
  }

  /**
   * Set active tab and populate content.
   * @param {Boolean} hasResults - Whether results have been found, or not.
   */
  function findActiveTab(hasResults) {
    const resultsTab = [...document.querySelectorAll(selectors.resultsTab)]

    if (hasResults) {
      const { products, articles } = resultsObj

      if (products.length) {
        const tab = resultsTab.find((el) => el.dataset.id === 'products')

        tab.classList.add(classes.active)
        getTabContent('products')

      } else if (!products.length && articles.length) {
        const tab = resultsTab.find((el) => el.dataset.id === 'pages')

        tab.classList.add(classes.active)
        getTabContent('pages')

      } else {
        const tab = resultsTab.find((el) => el.dataset.id === 'pages')

        tab.classList.add(classes.active)
        getTabContent('pages')
      }

    } else {
      buildEmptyResultsMain()
    }
  }

  /**
   * Handle results found.
   * @param {Object} results - Results object.
   */
  function handleResults(results) {
    const resultsHeader = buildResultsHeader(results)

    resultsObj = results
    nodeSelectors.resultsWrap.innerHTML = ''
    nodeSelectors.resultsWrap.insertAdjacentHTML('afterbegin', resultsHeader)
    nodeSelectors.featuredProductsWrap.classList.remove(classes.active)
    setHeaderListeners()
    findActiveTab(true)
  }

  /**
   * Handle no results found.
   */
  function handleNoResults() {
    const resultsHeader = buildResultsHeader(false)

    resultsObj = []
    nodeSelectors.resultsWrap.innerHTML = ''
    nodeSelectors.resultsWrap.insertAdjacentHTML('afterbegin', resultsHeader)
    setHeaderListeners()
    findActiveTab(false)
  }

  /**
   * Build suggested keywords markup.
   * @param {Array} suggested - Array of keywords.
   * @returns {String}
   */
  function buildKeywords(suggested) {
    let markup = `
      <h3 class="search-drawer__suggested-title">
        ${window.basis.strings.search.suggestedKeywords}
      </h3>
    `

    suggested.forEach((term) => {
      markup += `
        <button
          class="search-drawer__suggested-keyword"
          type="button"
          js-predictive-search-results="keyword"
          data-id="${term}"
        >
          ${term}
        </button>
      `
    })

    return markup
  }

  /**
   * Set event listeners on keywords after render.
   */
  function setKeywordListeners() {
    const keyword = document.querySelectorAll(selectors.keyword)

    keyword.forEach((word) => {
      on('click', word, () => {
        const value = word.dataset.id

        nodeSelectors.searchInput.value = value
        window.basis.EventBus.emit('search-change', value)
      })
    })
  }

  /**
   * Set default state of component.
   */
  function setDefaultState() {
    resultsObj = []
    nodeSelectors.resultsWrap.innerHTML = ''
    nodeSelectors.resultsWrap.insertAdjacentHTML('afterbegin', suggestedTerms)
    nodeSelectors.featuredProductsWrap.classList.add(classes.active)
    setKeywordListeners()
  }

  /**
   * Listen for basis.EventBus events.
   */
  function setEventBusListeners() {
    window.basis.EventBus.listen('search-results', (results) => {
      handleResults(results)
    })

    window.basis.EventBus.listen('no-search-results', (terms) => {
      handleNoResults(terms)
    })

    window.basis.EventBus.listen('search-clear', () => {
      setDefaultState()
    })

    window.basis.EventBus.listen('search-change', (value) => {
      if (value) {
        return
      }

      setDefaultState()
    })
  }

  /**
   * Initialize component.
   */
  function init() {
    const suggested = window.basis.predictiveSearch.suggestedTerms.split(',')

    suggestedTerms = buildKeywords(suggested)

    setDefaultState()
    setEventBusListeners()
  }

  /**
   * Expose public interface.
   */
  return Object.freeze({
    init,
  })
}
